import { waLink, track } from "@/constants/site";
import Reveal from "@/components/base/Reveal";

interface ServiceItem {
  icon: string;
  name: string;
  description: string;
}

const CATEGORIES: {
  id: string;
  eyebrow: string;
  title: string;
  items: ServiceItem[];
}[] = [
  {
    id: "servicos",
    eyebrow: "Assistência",
    title: "Manutenção e recuperação",
    items: [
      {
        icon: "ri-settings-4-line",
        name: "Manutenção de motores elétricos",
        description: "Serviço técnico para restaurar desempenho e confiabilidade.",
      },
      {
        icon: "ri-calendar-check-line",
        name: "Manutenção preventiva",
        description: "Inspeções planejadas para reduzir paradas e custos.",
      },
      {
        icon: "ri-alert-line",
        name: "Manutenção corretiva",
        description: "Atendimento para falhas e paradas inesperadas.",
      },
      {
        icon: "ri-loop-left-line",
        name: "Rebobinamento",
        description: "Rebobinamento de motores e equipamentos eletromecânicos.",
      },
      {
        icon: "ri-refresh-line",
        name: "Recuperação de motores",
        description: "Recuperação técnica para prolongar a vida útil.",
      },
      {
        icon: "ri-search-eye-line",
        name: "Diagnóstico técnico",
        description: "Avaliação para identificar a causa e a solução adequada.",
      },
      {
        icon: "ri-timer-line",
        name: "Balanceamento",
        description: "Correção de vibração para operação estável.",
      },
      {
        icon: "ri-drop-line",
        name: "Bombas",
        description: "Manutenção e recuperação de bombas industriais.",
      },
      {
        icon: "ri-settings-5-line",
        name: "Redutores e motoredutores",
        description: "Serviço técnico para sistemas de transmissão.",
      },
    ],
  },
  {
    id: "produtos",
    eyebrow: "Produtos",
    title: "Motores e equipamentos",
    items: [
      {
        icon: "ri-cpu-line",
        name: "Motores elétricos",
        description: "Motores industriais para as mais diversas aplicações.",
      },
      {
        icon: "ri-recycle-line",
        name: "Motores recondicionados",
        description: "Alternativa de desempenho com investimento inteligente.",
      },
      {
        icon: "ri-water-flash-line",
        name: "Bombas e motobombas",
        description: "Soluções de bombeamento para indústria e saneamento.",
      },
      {
        icon: "ri-settings-5-line",
        name: "Redutores",
        description: "Redutores de velocidade para sistemas de transmissão.",
      },
      {
        icon: "ri-settings-2-line",
        name: "Motoredutores",
        description: "Conjuntos compactos motor + redutor para aplicações.",
      },
      {
        icon: "ri-tools-line",
        name: "Componentes",
        description: "Peças e componentes para manutenção eletromecânica.",
      },
    ],
  },
  {
    id: "solucoes",
    eyebrow: "Automação e controle",
    title: "Tecnologia para sua operação",
    items: [
      {
        icon: "ri-line-chart-line",
        name: "Inversores de frequência",
        description: "Controle de velocidade e eficiência energética.",
      },
      {
        icon: "ri-flashlight-line",
        name: "Soft-starters",
        description: "Partida suave para reduzir picos e desgaste.",
      },
      {
        icon: "ri-robot-2-line",
        name: "Automação industrial",
        description: "Soluções para processos automatizados.",
      },
      {
        icon: "ri-toggle-line",
        name: "Controles",
        description: "Painéis e sistemas de comando e proteção.",
      },
    ],
  },
];

function ServiceCard({ item }: { item: ServiceItem }) {
  const msg = `Olá! Gostaria de saber mais sobre ${item.name}.`;
  return (
    <div className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-all duration-200 hover:border-primary-300 hover:bg-background-100/50">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
          <i className={`${item.icon} text-xl`} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h4 className="text-base font-bold text-foreground-900">{item.name}</h4>
          <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.description}</p>
        </div>
      </div>
      <a
        href={waLink(msg)}
        target="_blank"
        rel="noreferrer"
        onClick={() => track("quote_start", { service: item.name })}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary-700 hover:text-primary-800"
      >
        Solicitar
        <i className="ri-arrow-right-up-line" aria-hidden="true" />
      </a>
    </div>
  );
}

export default function Services() {
  return (
    <section className="bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <Reveal>
          <p className="text-center text-xs font-bold uppercase tracking-[0.16em] text-accent-600">
            Serviços e soluções
          </p>
          <h2 className="mt-3 text-center font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
            Soluções para manter sua operação em movimento
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-base text-foreground-600">
            Assistência técnica, produtos e automação para quem não pode parar.
          </p>
        </Reveal>

        {CATEGORIES.map((cat, idx) => (
          <div key={cat.id} id={cat.id} className={`mt-14 scroll-mt-24 ${idx > 0 ? "pt-4" : ""}`}>
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-8 w-1 rounded-full bg-accent-500" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-600">
                    {cat.eyebrow}
                  </p>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {cat.title}
                  </h3>
                </div>
              </div>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cat.items.map((item, i) => (
                <Reveal key={item.name} delay={i * 40}>
                  <ServiceCard item={item} />
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}